import apiClient from './client'
import type { Tag } from './tags'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ConversationMessage {
  id: string
  conversation_id: string
  role: 'user' | 'assistant'
  content: string
  content_format: 'markdown' | 'html'
  sources_consulted: string[] | null
  search_queries: string[] | null
  notes_found: number
  external_sources_used: number
  request_tokens: number
  response_tokens: number
  total_tokens: number
  creation_time: number
}

export interface ConversationListItem {
  id: string
  title: string
  mode: 'smart_search' | 'manual'
  creation_time: number
  tags: Tag[]
  last_message_preview: string
  message_count: number
}

export interface ConversationDetail {
  id: string
  title: string
  mode: 'smart_search' | 'manual'
  creation_time: number
  tags: Tag[]
  pinned_note_ids: string[] | null
  messages: ConversationMessage[]
  message_count: number
}

export interface ConversationsResponse {
  data: ConversationListItem[]
  count: number
  limit: number
  offset: number
}

export interface ConversationFilter {
  selected_tags?: Tag[]
  search?: string
}

export interface SendMessageRequest {
  question: string
  conversation_id?: string | null
  tags?: Tag[] | null
  pinned_note_ids?: string[] | null
  mode?: 'smart_search' | 'manual'
}

export interface SendMessageResponse {
  conversation_id: string
  conversation_title: string
  message: ConversationMessage
  /** Total messages (user + assistant) now in the conversation */
  message_count: number
  /** True when the conversation has reached the hard per-conversation limit */
  at_message_limit: boolean
}

export interface ConversationPatch {
  title?: string
  pinned_note_ids?: string[] | null
  mode?: 'smart_search' | 'manual'
}

export interface AppendToNoteRequest {
  message_id: string
  target_note_id: string
}

// ── API functions ──────────────────────────────────────────────────────────────

/** Send a message — starts a new conversation if conversation_id is omitted. */
export async function sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
  const response = await apiClient.post<SendMessageResponse>('/agent/query-agent', request)
  return response.data
}

/** List conversations with optional tag + title filtering and pagination. */
export async function getConversations(
  filter: ConversationFilter = {},
  skip = 0,
  limit = 30
): Promise<ConversationsResponse> {
  const response = await apiClient.post<ConversationsResponse>(
    `/agent/conversations?skip=${skip}&limit=${limit}`,
    filter
  )
  return response.data
}

/** Load a single conversation with all its messages. */
export async function getConversation(id: string): Promise<ConversationDetail> {
  const response = await apiClient.get<{ conversation: ConversationDetail }>(
    `/agent/conversations/${id}`
  )
  // Backend returns { conversation: {...} } via ConversationPublic wrapper
  return response.data.conversation ?? (response.data as unknown as ConversationDetail)
}

/** Delete a single conversation. */
export async function deleteConversation(id: string): Promise<void> {
  await apiClient.delete(`/agent/conversations/${id}`)
}

/** Bulk delete conversations. */
export async function bulkDeleteConversations(ids: string[]): Promise<void> {
  await apiClient.delete('/agent/conversations', { data: ids })
}

/** Update conversation metadata (title, mode, pinned_note_ids). */
export async function updateConversation(
  id: string,
  patch: ConversationPatch
): Promise<ConversationDetail> {
  const response = await apiClient.patch<{ conversation: ConversationDetail }>(
    `/agent/conversations/${id}`,
    patch
  )
  return response.data.conversation ?? (response.data as unknown as ConversationDetail)
}

/** Convert a conversation (or specific message) to a new note. */
export async function convertMessageToNote(
  conversationId: string,
  messageId?: string
): Promise<{ note_id: string }> {
  const params = messageId ? `?message_id=${messageId}` : ''
  const response = await apiClient.post<{ note_id: string }>(
    `/agent/conversations/${conversationId}/create-note${params}`
  )
  return response.data
}

/** Append a specific assistant message to an existing note. */
export async function appendMessageToNote(
  conversationId: string,
  messageId: string,
  targetNoteId: string
): Promise<void> {
  const body: AppendToNoteRequest = { message_id: messageId, target_note_id: targetNoteId }
  await apiClient.post(`/agent/conversations/${conversationId}/append-to-note`, body)
}

